import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

function money(v: number): string {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ROOT_DOMAIN = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN ?? 'lvh.me:3000';

function storeUrl(subdomain: string): string {
  const scheme = ROOT_DOMAIN.includes('lvh.me') ? 'http' : 'https';
  return `${scheme}://${subdomain}.${ROOT_DOMAIN}`;
}

function when(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.round(h / 24)} d`;
}

/** P5 · Início — cabeçalho + tiles + "precisa de você" + últimas vendas (camada L4). */
export function Inicio() {
  const { claims } = useAuth();
  const name = (claims?.name ?? claims?.email ?? 'por aqui').split(' ')[0];

  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const summary = useQuery({ queryKey: ['salesSummary'], queryFn: () => api.salesSummary() });
  const balance = useQuery({ queryKey: ['balance'], queryFn: () => api.balance() });
  const pending = useQuery({ queryKey: ['pendingShipments'], queryFn: () => api.pendingShipmentCount() });
  const recent = useQuery({ queryKey: ['orders', 0, 5], queryFn: () => api.orders({ page: 0, size: 5 }) });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>
          {greeting()}, {name}
        </h1>
        <p>O resumo da sua loja hoje.</p>
      </div>

      <div className={ui.actions}>
        <Link className={`${ui.btn} ${ui.btnPrimary}`} to="/produtos">
          + Criar produto
        </Link>
        {store.data ? (
          <a
            className={`${ui.btn} ${ui.btnGhost}`}
            href={storeUrl(store.data.subdomain)}
            target="_blank"
            rel="noreferrer"
          >
            Ver minha loja
          </a>
        ) : null}
      </div>

      <section className={ui.stats} aria-label="Resumo de vendas">
        <div className={ui.stat}>
          <span className={ui.statLbl}>Vendas hoje</span>
          <div className={ui.statVal}>
            <span className={ui.statCur}>R$</span>
            {summary.data ? money(summary.data.todayTotal) : '—'}
          </div>
          <span className={ui.statSub}>{summary.data ? `${summary.data.todayCount} pedido(s)` : ''}</span>
        </div>
        <div className={ui.stat}>
          <span className={ui.statLbl}>Vendas na semana</span>
          <div className={ui.statVal}>
            <span className={ui.statCur}>R$</span>
            {summary.data ? money(summary.data.weekTotal) : '—'}
          </div>
        </div>
        <div className={ui.stat}>
          <span className={ui.statLbl}>A receber</span>
          <div className={ui.statVal}>
            <span className={ui.statCur}>R$</span>
            {balance.data ? money(balance.data.available) : '—'}
          </div>
          <span className={ui.statSub}>
            <Link to="/dinheiro">Ver extrato</Link>
          </span>
        </div>
      </section>

      {pending.data ? (
        <div className={ui.card}>
          <div className={ui.attnH}>
            <span className={ui.attnPip} aria-hidden="true" />
            <b>Precisa de você</b>
          </div>
          <ul>
            <li className={ui.attnList}>
              <span>
                <b>{pending.data}</b> pedido(s) físico(s) aguardando envio
              </span>
              <Link to="/vendas">Ver vendas</Link>
            </li>
          </ul>
        </div>
      ) : null}

      <section>
        <div className={ui.secH}>
          <h2>Últimas vendas</h2>
          <Link to="/vendas">Ver todas</Link>
        </div>
        <div className={ui.card}>
          {!recent.data?.items.length ? (
            <p className={ui.empty}>Nenhuma venda ainda.</p>
          ) : (
            <div className={ui.tblScroll}>
              <table className={ui.tbl}>
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Comprador</th>
                    <th>Produto</th>
                    <th>Meio</th>
                    <th className="r">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.data.items.map((o) => (
                    <tr key={o.orderId}>
                      <td>
                        <span className={ui.mono}>#{o.number}</span>
                        <div className={ui.faint}>{when(o.createdAt)}</div>
                      </td>
                      <td>
                        <b>{o.customerName || o.customerEmail}</b>
                      </td>
                      <td className={ui.muted}>{o.productName}</td>
                      <td className={ui.mono}>{o.paymentMethod === 'card' ? 'CARTÃO' : 'PIX'}</td>
                      <td className="r">
                        <span className={ui.money}>R$ {money(o.amount)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
